import type { QueryEntity } from "../types";

const ROLE_COLOR: Record<string, string> = {
  asks: "#a0522d",
  intervention: "#2b5675",
  outcome: "#2f6b4f",
  condition: "#6b6f8a",
  population: "#6b6f8a",
  comparator: "#8a857a",
  exposure: "#2b5675",
};
const ROLE_NAME: Record<string, string> = {
  asks: "asks",
  intervention: "intervention",
  outcome: "outcome",
  condition: "population",
  population: "population",
  comparator: "comparator",
  exposure: "exposure",
};

/** One word per entity: for "asks" the kind of answer, e.g. "whether it works: result + significance" -> "result". */
const askedFor = (label: string) =>
  (label.split(":").pop() ?? "").split(/[+,;]| and /)[0].trim().split(/\s+/)[0] || "asks";
const roleName = (role: string, entity: QueryEntity) =>
  role === "asks" ? (entity.label ? askedFor(entity.label) : "asks") : (ROLE_NAME[role] ?? role.replace(/_/g, " "));
const color = (role: string) => ROLE_COLOR[role] ?? "#7a7a7a";

/** The question with the words of each entity the builder found marked and its role written under them. */
export const QueryEntities = ({
  question,
  entities,
}: {
  question: string;
  entities: Record<string, QueryEntity> | null | undefined;
}) => {
  const placed = Object.entries(entities ?? {})
    .filter(([, e]) => e?.v && typeof e.s === "number" && typeof e.e === "number" && e.s < e.e && e.e <= question.length)
    .sort((a, b) => a[1].s! - b[1].s!);
  const parts: React.ReactNode[] = [];
  let at = 0;
  for (const [role, e] of placed) {
    if (e.s! < at) continue;
    parts.push(question.slice(at, e.s));
    parts.push(
      <mark
        key={role}
        className="relative rounded-[2px] bg-transparent px-0.5 text-zinc-900"
        style={{
          background: `color-mix(in srgb, ${color(role)} 12%, transparent)`,
          boxShadow: `inset 0 -2px 0 color-mix(in srgb, ${color(role)} 55%, transparent)`,
        }}
      >
        {question.slice(e.s, e.e)}
        <span
          data-entity-label
          className="font-ui pointer-events-none absolute left-0.5 top-full mt-0.5 whitespace-nowrap text-[9px] font-semibold uppercase leading-none tracking-wide"
          style={{ color: color(role) }}
        >
          {roleName(role, e)}
        </span>
      </mark>,
    );
    at = e.e!;
  }
  parts.push(question.slice(at));
  return (
    <p aria-label="Question entities" className="pb-3 text-[15px] leading-[2.6] text-zinc-900">
      {parts}
    </p>
  );
};
